import React, { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import TableHeaderComponent, {
  TableHeaderColumnType,
  TableHeaderItemOrderProps,
} from './TableHeader.component';
import SpinnerComponent, { StyledSpinnerView } from '../Spinner.component';
import TablePaginationComponent from './TablePagination.component';
import { PaginationType } from '../../redux/App/App.type';
import { Transition } from 'react-transition-group';
import { SortTypes } from '../../types/sort.types';
import { getSort } from '../../services/router.service';

export interface TableProps extends React.AllHTMLAttributes<HTMLDivElement> {
  header?: TableHeaderColumnType[];
  pagination?: PaginationType;
  onSortChange?: Function;
  onPageChange?: Function;
  loading?: boolean;
  isFavouriteChanged?: boolean;
  setFavouriteChanged?: (isFavouriteChanged: boolean) => void;
  type?: 'flex';
}

const StyledTableLoading = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;

  ${StyledSpinnerView} {
    color: white;
  }
`;
export const StyledTableRows = styled.div`
  overflow: auto;
`;
export const StyledTable = styled.div`
  position: relative;
`;

const TableComponent = ({
  header,
  loading,
  pagination,
  onSortChange,
  onPageChange,
  children,
  isFavouriteChanged,
  setFavouriteChanged,
  type,
}: TableProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const theme = useTheme() as any;
  const [order, setOrder] = useState<TableHeaderItemOrderProps>();

  useEffect(() => {
    if (order) return;

    const defaultSortOrder = header
      ? header
          .filter((item: TableHeaderColumnType) => {
            return (
              item.sortable && item.sort && item.sort !== SortTypes.DEFAULT
            );
          })
          .map(({ dataProperty, sort }: TableHeaderColumnType) => ({
            dataProperty: dataProperty || 'date',
            sort: sort || SortTypes.DEFAULT,
          }))
      : [
          {
            dataProperty: 'date',
            sort: SortTypes.DEFAULT,
          },
        ];
    setOrder(defaultSortOrder[0] as TableHeaderColumnType);
  }, [header]);

  useEffect(() => {
    const defaultSort = getSort(window.location.search);
    if (!defaultSort) {
      const defaultHeaderSort = header
        ? header.filter((item: TableHeaderColumnType) => {
            return item.sortable && item.sort !== SortTypes.DEFAULT;
          })
        : [
            {
              dataProperty: 'date',
              sort: SortTypes.DEFAULT,
            },
          ];
      setOrder(defaultHeaderSort[0] as TableHeaderColumnType);
      return;
    }

    const isDesc = defaultSort[0] === '-';
    let target = defaultSort;
    if (isDesc) {
      target = target.slice(1, target.length);
    }

    setOrder({
      dataProperty: target,
      sort: isDesc ? SortTypes.DESC : SortTypes.ASC,
    });
  }, [window.location.search]);

  useEffect(() => {
    !loading && !isFavouriteChanged && wrapperRef.current && wrapperRef.current.scrollTo(0, 0);

    if (!loading && isFavouriteChanged && setFavouriteChanged) {
      setTimeout(() => setFavouriteChanged(false), 300);
    }
  }, [loading]);

  const handleSetOrder = (order: TableHeaderItemOrderProps) => {
    setOrder(order);
    onSortChange && onSortChange(order);
  };

  return (
    <StyledTable>
      {header && (
        <TableHeaderComponent
          columns={header}
          order={order}
          onSort={handleSetOrder}
          type={type}
        />
      )}
      <StyledTableRows ref={wrapperRef}>{children}</StyledTableRows>

      {pagination && pagination.lastPage > 1 && (
        <TablePaginationComponent
          pageChange={(page: number) => onPageChange && onPageChange(page)}
          pageCount={pagination.lastPage || 0}
          total={pagination.total}
          currentPage={pagination.currentPage || 1}
        />
      )}

      <Transition
        in={loading}
        unmountOnExit={true}
        timeout={{
          exit: 300,
        }}
      >
        {state => (
          <StyledTableLoading
            style={{
              ...theme.animations.defaultState,
              ...theme.animations.defaultSteps[state],
            }}
          >
            <SpinnerComponent />
          </StyledTableLoading>
        )}
      </Transition>
    </StyledTable>
  );
};

export default TableComponent;
